import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, StepContent,
  Chip, Tooltip, CircularProgress, Alert,
  Autocomplete, Checkbox, FormControlLabel, FormGroup,
  Divider, Radio, RadioGroup, FormControl, FormLabel,
  Table, TableHead, TableBody, TableRow, TableCell,
  Accordion, AccordionSummary, AccordionDetails,
  MenuItem,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import TableChartIcon from '@mui/icons-material/TableChart';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../../services/api.js';
import { saveAs } from 'file-saver';
import { formatHebrewDate } from '../../utils/hebrewDate';
import {
  EMPTY_RANGE, EMPTY_LINK, EMPTY_MAX_RULE, EMPTY_STAR_RULE, CHELEK_OPTIONS,
  serializeLibraryLinks, serializeMaxQuestionRules, serializeStarEveryNRules,
} from './issuanceModel.js';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import ExamIssuanceWizardNext from './ExamIssuanceWizardNext.jsx';

const IS_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

/** בורר דילוג כוכבית (ברירת מחדל / כלל / דריסה) */
function StarEveryNSelect({ value, onChange, label = 'דילוג כוכבית', sx = {} }) {
  const n = Number(value) || 0;
  const isPreset = n === 0 || n === 2 || n === 3 || n === 4 || n === 5;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ...sx }}>
      <TextField
        select
        size="small"
        label={label}
        value={isPreset ? String(n) : 'custom'}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'custom') {
            onChange(n >= 2 && ![2, 3, 4, 5].includes(n) ? n : 6);
            return;
          }
          onChange(Number(v) || 0);
        }}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="0">ללא</MenuItem>
        <MenuItem value="2">כל שני</MenuItem>
        <MenuItem value="3">כל שלישי</MenuItem>
        <MenuItem value="4">כל רביעי</MenuItem>
        <MenuItem value="5">כל חמישי</MenuItem>
        <MenuItem value="custom">מספר אחר…</MenuItem>
      </TextField>
      {n >= 2 && ![2, 3, 4, 5].includes(n) && (
        <TextField
          type="number"
          size="small"
          label="כל כמה שאלות"
          value={n}
          onChange={(e) => onChange(Math.max(2, parseInt(e.target.value, 10) || 2))}
          inputProps={{ min: 2, step: 1 }}
          sx={{ width: 140 }}
        />
      )}
    </Box>
  );
}

/** ברירת מחדל + כללי טווח קוד (משותף ל-PDF ו-Word) */
function MaxQuestionsSettings({
  maxQuestions,
  setMaxQuestions,
  maxQuestionRules,
  setMaxQuestionRules,
}) {
  const updateRule = (idx, field, raw) => {
    setMaxQuestionRules((rules) => rules.map((r, i) => (
      i === idx ? { ...r, [field]: raw === '' ? '' : String(raw).replace(/\D/g, '') } : r
    )));
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" fontWeight={600}>מספר שאלות מקסימלי (ברירת מחדל):</Typography>
        <TextField
          type="number"
          size="small"
          value={maxQuestions === 0 ? '' : maxQuestions}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              setMaxQuestions(0);
              return;
            }
            setMaxQuestions(Math.max(0, parseInt(raw, 10) || 0));
          }}
          placeholder="ללא הגבלה"
          inputProps={{ min: 0, step: 1 }}
          sx={{ width: 100 }}
        />
        <Typography variant="caption" color="text.secondary">
          (ריק/0 = כל השאלות; מעל המקסימום — דגימה אקראית)
        </Typography>
      </Box>

      {!IS_GEMARA && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>לפי טווח קוד מבחן</Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setMaxQuestionRules((rules) => [...rules, { ...EMPTY_MAX_RULE }])}
            >
              הוסף כלל
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            הכלל הראשון שמתאים לקוד המבחן מנצח. דריסה פרטנית בתצוגה המקדימה גוברת על הכללים.
          </Typography>
          {maxQuestionRules.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              אין כללי טווח — כל המבחנים משתמשים בברירת המחדל.
            </Typography>
          )}
          {maxQuestionRules.map((rule, idx) => (
            <Box
              key={idx}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}
            >
              <TextField
                label="מקוד"
                size="small"
                type="number"
                value={rule.fromCode}
                onChange={(e) => updateRule(idx, 'fromCode', e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 100 }}
              />
              <TextField
                label="עד קוד"
                size="small"
                type="number"
                value={rule.toCode}
                onChange={(e) => updateRule(idx, 'toCode', e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 100 }}
              />
              <TextField
                label="מקס' שאלות"
                size="small"
                type="number"
                value={rule.maxQuestions}
                onChange={(e) => updateRule(idx, 'maxQuestions', e.target.value)}
                placeholder="0 = ללא"
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 120 }}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => setMaxQuestionRules((rules) => rules.filter((_, i) => i !== idx))}
                title="הסר כלל"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/** כוכבית — ברירת מחדל + כללי טווח קוד */
function StarEveryNSettings({
  starEveryN,
  setStarEveryN,
  starEveryNRules,
  setStarEveryNRules,
}) {
  const updateRuleField = (idx, field, raw) => {
    setStarEveryNRules((rules) => rules.map((r, i) => (
      i === idx ? { ...r, [field]: raw === '' ? '' : String(raw).replace(/\D/g, '') } : r
    )));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Typography fontWeight={600} sx={{ mb: 1 }}>כוכבית בתחילת שאלות</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        מסמן בכוכבית שאלות לפי דילוג (למשל כל שני / כל שלישי). כוכביות שסומנו בספרייה נשמרות.
      </Typography>
      <StarEveryNSelect
        label="דילוג כוכבית (ברירת מחדל)"
        value={starEveryN}
        onChange={setStarEveryN}
      />

      {!IS_GEMARA && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>לפי טווח קוד מבחן</Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setStarEveryNRules((rules) => [...rules, { ...EMPTY_STAR_RULE }])}
            >
              הוסף כלל
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            הכלל הראשון שמתאים לקוד המבחן מנצח. דריסה פרטנית בתצוגה המקדימה גוברת על הכללים.
          </Typography>
          {starEveryNRules.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              אין כללי טווח — כל המבחנים משתמשים בברירת המחדל.
            </Typography>
          )}
          {starEveryNRules.map((rule, idx) => (
            <Box
              key={idx}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}
            >
              <TextField
                label="מקוד"
                size="small"
                type="number"
                value={rule.fromCode}
                onChange={(e) => updateRuleField(idx, 'fromCode', e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 100 }}
              />
              <TextField
                label="עד קוד"
                size="small"
                type="number"
                value={rule.toCode}
                onChange={(e) => updateRuleField(idx, 'toCode', e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 100 }}
              />
              <StarEveryNSelect
                value={rule.starEveryN}
                onChange={(v) => setStarEveryNRules((rules) => rules.map((r, i) => (
                  i === idx ? { ...r, starEveryN: v } : r
                )))}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => setStarEveryNRules((rules) => rules.filter((_, i) => i !== idx))}
                title="הסר כלל"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

/**
 * ExamIssuanceWizard — אשף הנפקת בחינות
 *
 * שלבים:
 * 1. בחירת מועד/מבחן + סינון (עיר, כולל, טווח סימנים)
 * 2. בחירת סוג פלט (PDF / Excel / Word) + הגדרות + כוכבית לפי דילוג
 * 3. תצוגה מקדימה + הנפקה
 */
/** In the new design the same props render ExamIssuanceWizardNext. */
const ExamIssuanceWizard = (props) => (useCkInside() ? <ExamIssuanceWizardNext {...props} /> : <ExamIssuanceWizardLegacy {...props} />);

const ExamIssuanceWizardLegacy = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // state for skipped groups warning after issuance
  const [skippedWarning, setSkippedWarning] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);

  // סינונים
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [kolels, setKolels] = useState([]);
  const [selectedKolels, setSelectedKolels] = useState([]);
  // מקור כמויות (הלכתא בלבד): both | gabbai | registrations
  const [sourceMode, setSourceMode] = useState(IS_GEMARA ? 'registrations' : 'both');

  // שלב 2 — בחירת סוג פלט
  const [outputType, setOutputType] = useState('pdf'); // 'pdf' | 'excel' | 'word'
  // כוכבית לפי דילוג: 0 = כבוי; 2 = כל שני; 3 = כל שלישי...
  const [starEveryN, setStarEveryN] = useState(0);
  const [starEveryNRules, setStarEveryNRules] = useState([]);
  const [starEveryNByGroup, setStarEveryNByGroup] = useState({});
  const [designElements, setDesignElements] = useState([]); // all available
  // אלמנטים שנבחרו — מוסיפים עם "+"
  const [activeSlots, setActiveSlots] = useState([]); // e.g. ['top', 'bottom', 'frame']
  const [elementsLoading, setElementsLoading] = useState(false);
  const [selectedTop, setSelectedTop] = useState(null);
  const [topFirstOnly, setTopFirstOnly] = useState(true);
  const [selectedBottom, setSelectedBottom] = useState(null);
  const [bottomFirstOnly, setBottomFirstOnly] = useState(true);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [frameFirstOnly, setFrameFirstOnly] = useState(true);
  // דפי שער/סיום (PDF)
  const [extraPageTemplates, setExtraPageTemplates] = useState([]);
  const [selectedCoverPage, setSelectedCoverPage] = useState(null);
  const [selectedEndPage, setSelectedEndPage] = useState(null);
  const [coverPdfFile, setCoverPdfFile] = useState(null);
  const [endPdfFile, setEndPdfFile] = useState(null);
  const [includeTitle, setIncludeTitle] = useState(true);
  const [includeExamineeCode, setIncludeExamineeCode] = useState(true);
  const [includeExamCode, setIncludeExamCode] = useState(true);
  const [showSimanimHeader, setShowSimanimHeader] = useState(true);
  const [answerLinesCount, setAnswerLinesCount] = useState(4);
  const [maxQuestions, setMaxQuestions] = useState(20);
  const [maxQuestionRules, setMaxQuestionRules] = useState([]);
  const [maxQuestionsByGroup, setMaxQuestionsByGroup] = useState({});
  const [difficulties, setDifficulties] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [libraryLinks, setLibraryLinks] = useState([]);
  const [introText, setIntroText] = useState('');
  const [outroText, setOutroText] = useState('');
  const [issuanceName, setIssuanceName] = useState('');

  // שלב 3 — תצוגה מקדימה
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  // טעינת מועדים/מבחנים
  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setError('');
    setGenerated(false);
    setPreview(null);
    setSkippedWarning(null);
    setIntroText('');
    setOutroText('');
    setOutputType('pdf');
    setSelectedDifficulties([]);
    setLibraryLinks([]);
    setSelectedCities([]);
    setSelectedKolels([]);
    setStarEveryN(0);
    setStarEveryNRules([]);
    setStarEveryNByGroup({});
    setShowSimanimHeader(true);
    setMaxQuestions(20);
    setMaxQuestionRules([]);
    setMaxQuestionsByGroup({});
    setSelectedCoverPage(null);
    setSelectedEndPage(null);
    setCoverPdfFile(null);
    setEndPdfFile(null);

    const load = async () => {
      try {
        const diffPromise = api.get('/exam-library/difficulties');
        if (IS_GEMARA) {
          const [{ data: periodsData }, diffsRes] = await Promise.all([
            api.get('/exam-periods'),
            diffPromise,
          ]);
          setPeriods(periodsData.data || []);
          setDifficulties(Array.isArray(diffsRes.data) ? diffsRes.data : []);
        } else {
          const [periodsRes, examsRes, diffsRes] = await Promise.all([
            api.get('/exam-periods'),
            api.get('/exams?pageSize=100000'),
            diffPromise,
          ]);
          setPeriods(periodsRes.data.data || []);
          setExams(examsRes.data.data || []);
          setDifficulties(Array.isArray(diffsRes.data) ? diffsRes.data : []);
        }
      } catch {
        setError('שגיאה בטעינת נתונים');
      }
      try {
        const { data } = await api.get('/kolels', { params: { pageSize: 500, status: 'active' } });
        setKolels(data.data || []);
      } catch {
        setKolels([]);
      }
    };
    load();
  }, [open]);

  // טען אלמנטים עיצוביים + דפי שער/סיום
  useEffect(() => {
    if (!open) return;
    setElementsLoading(true);
    Promise.all([
      api.get('/exam-blanks'),
      api.get('/exam-extra-pages'),
    ])
      .then(([blanksRes, extraRes]) => {
        const list = (blanksRes.data.data || []).filter((e) => e.status !== 'inactive');
        setDesignElements(list);
        const slots = [];
        const defTop = list.find((b) => b.position === 'top' && b.isDefault);
        const defBottom = list.find((b) => b.position === 'bottom' && b.isDefault);
        const defFrame = list.find((b) => b.position === 'frame' && b.isDefault);
        if (defTop) { setSelectedTop(defTop); slots.push('top'); }
        if (defBottom) { setSelectedBottom(defBottom); slots.push('bottom'); }
        if (defFrame) { setSelectedFrame(defFrame); slots.push('frame'); }
        setActiveSlots(slots);

        const extraList = (extraRes.data.data || []).filter((e) => e.status !== 'inactive');
        setExtraPageTemplates(extraList);
        const defCover = extraList.find((p) => p.position === 'cover' && p.isDefault);
        const defEnd = extraList.find((p) => p.position === 'end' && p.isDefault);
        if (defCover) setSelectedCoverPage(defCover);
        if (defEnd) setSelectedEndPage(defEnd);
      })
      .catch((err) => {
        console.error('[ExamIssuanceWizard] failed to load design/extra pages:', err?.response?.status, err?.response?.data);
      })
      .finally(() => setElementsLoading(false));
  }, [open]);

  // טען ערים ייחודיות מהרשמות + הזמנות גבאים (הלכתא)
  useEffect(() => {
    if (!open) return;
    const loadCities = async () => {
      const citySet = new Set();
      try {
        const { data } = await api.get('/registrations?all=1');
        (data.data || []).forEach((r) => {
          if (r.memberId?.city) citySet.add(r.memberId.city);
          if (r.location) citySet.add(String(r.location).trim());
        });
      } catch { /* ignore */ }
      if (!IS_GEMARA) {
        try {
          const { data } = await api.get('/gabbai-orders/summary');
          (data.data || []).forEach((row) => {
            if (row.location) citySet.add(String(row.location).trim());
          });
        } catch { /* ignore */ }
      }
      setCities([...citySet].filter(Boolean).sort((a, b) => a.localeCompare(b, 'he')));
    };
    loadCities();
  }, [open]);

  // מילוי טקסטים ממבחן נבחר
  useEffect(() => {
    if (!selectedExam) return;
    setIntroText(selectedExam.introText || '');
    setOutroText(selectedExam.graderNotes || '');
  }, [selectedExam]);

  // ── Library links (דריסת טווח סימנים להנפקה) ──
  const updateLinks = (fn) => setLibraryLinks((links) => fn(links || []));
  const addLink = () => updateLinks((links) => [...links, { ...EMPTY_LINK, ranges: [{ ...EMPTY_RANGE }] }]);
  const removeLink = (li) => updateLinks((links) => links.filter((_, i) => i !== li));
  const changeLinkChelek = (li, val) => updateLinks((links) =>
    links.map((l, i) => (i === li ? { ...l, chelek: val } : l))
  );
  const addRange = (li) => updateLinks((links) =>
    links.map((l, i) => (i === li ? { ...l, ranges: [...(l.ranges || []), { ...EMPTY_RANGE }] } : l))
  );
  const removeRange = (li, ri) => updateLinks((links) =>
    links.map((l, i) => (i === li ? { ...l, ranges: l.ranges.filter((_, j) => j !== ri) } : l))
  );
  const changeRange = (li, ri, field, val) => updateLinks((links) =>
    links.map((l, i) => (i === li
      ? { ...l, ranges: l.ranges.map((r, j) => (j === ri ? { ...r, [field]: val } : r)) }
      : l))
  );

  const buildIssuanceOptions = useCallback(() => {
    const options = {
      includeTitle,
      includeExamineeCode,
      includeExamCode,
      answerLinesCount,
      maxQuestions,
      difficulties: selectedDifficulties,
      introText,
      outroText,
      showSimanimHeader: !IS_GEMARA && showSimanimHeader,
    };
    const links = serializeLibraryLinks(libraryLinks);
    if (links) options.libraryLinks = links;
    const rules = serializeMaxQuestionRules(maxQuestionRules);
    if (rules) options.maxQuestionRules = rules;
    if (maxQuestionsByGroup && Object.keys(maxQuestionsByGroup).length) {
      options.maxQuestionsByGroup = maxQuestionsByGroup;
    }
    if (starEveryN >= 2) options.starEveryN = starEveryN;
    else options.starEveryN = 0;
    const starRules = serializeStarEveryNRules(starEveryNRules);
    if (starRules) options.starEveryNRules = starRules;
    if (starEveryNByGroup && Object.keys(starEveryNByGroup).length) {
      options.starEveryNByGroup = starEveryNByGroup;
    }
    return options;
  }, [
    includeTitle, includeExamineeCode, includeExamCode, answerLinesCount,
    maxQuestions, maxQuestionRules, maxQuestionsByGroup, selectedDifficulties,
    introText, outroText, showSimanimHeader, libraryLinks,
    starEveryN, starEveryNRules, starEveryNByGroup,
  ]);

  const applyFiltersToBody = useCallback((body) => {
    if (selectedCities.length) body.filters.cities = selectedCities;
    if (selectedKolels.length) {
      body.filters.kolels = selectedKolels.map((k) => String(k._id || k));
    }
    return body;
  }, [selectedCities, selectedKolels]);

  // תצוגה מקדימה
  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setError('');
    try {
      const body = applyFiltersToBody({
        filters: {},
        options: buildIssuanceOptions(),
        sourceMode: IS_GEMARA ? 'registrations' : sourceMode,
      });
      if (IS_GEMARA && selectedPeriod) body.examPeriodId = selectedPeriod._id;
      else if (!IS_GEMARA && selectedExam) body.examId = selectedExam._id;
      else if (!IS_GEMARA && selectedPeriod) body.examPeriodId = selectedPeriod._id;

      const { data } = await api.post('/exam-issuance/preview', body);
      setPreview(data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת תצוגה מקדימה');
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedPeriod, selectedExam, sourceMode, buildIssuanceOptions, applyFiltersToBody]);

  // כשעוברים לשלב 3 / משנים מקס' לקבוצה — טען preview (debounce)
  useEffect(() => {
    if (activeStep !== 2) return undefined;
    const t = setTimeout(() => loadPreview(), 400);
    return () => clearTimeout(t);
  }, [activeStep, loadPreview]);

  const hasValidSimanRange = !IS_GEMARA && !!serializeLibraryLinks(libraryLinks);

  const handleNext = () => {
    if (activeStep === 0) {
      if (!selectedPeriod && !selectedExam && !hasValidSimanRange) {
        setError(IS_GEMARA
          ? 'יש לבחור מועד'
          : 'יש לבחור מבחן, מועד, או טווח סימנים');
        return;
      }
    }
    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep(prev => prev - 1);
  };

  // הנפקה — הורדה מיידית
  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const body = applyFiltersToBody({
        filters: {},
        options: buildIssuanceOptions(),
        sourceMode: IS_GEMARA ? 'registrations' : sourceMode,
      });
      if (IS_GEMARA && selectedPeriod) body.examPeriodId = selectedPeriod._id;
      else if (!IS_GEMARA && selectedExam) body.examId = selectedExam._id;
      else if (!IS_GEMARA && selectedPeriod) body.examPeriodId = selectedPeriod._id;
      if (issuanceName.trim()) body.issuanceName = issuanceName.trim();

      if (outputType === 'excel') {
        const response = await api.post('/exam-issuance/export-excel', body, { responseType: 'blob' });
        const fileName = issuanceName.trim() ? `${issuanceName.trim()}.xlsx` : 'exam-issuance.xlsx';
        saveAs(response.data, fileName);
        setGenerated(true);
      } else {
        // PDF / Word
        if (outputType === 'pdf') {
          const de = {};
          if (selectedTop) de.top = { id: selectedTop._id, firstPageOnly: topFirstOnly };
          if (selectedBottom) de.bottom = { id: selectedBottom._id, firstPageOnly: bottomFirstOnly };
          if (selectedFrame) de.frame = { id: selectedFrame._id, firstPageOnly: frameFirstOnly };
          if (Object.keys(de).length) body.designElements = de;

          const extraPages = {};
          if (!coverPdfFile && selectedCoverPage) extraPages.cover = { id: selectedCoverPage._id };
          if (!endPdfFile && selectedEndPage) extraPages.end = { id: selectedEndPage._id };
          if (Object.keys(extraPages).length) body.extraPages = extraPages;
        }

        const endpoint = outputType === 'word'
          ? '/exam-issuance/generate-word'
          : '/exam-issuance/generate-pdf';

        let response;
        if (outputType === 'pdf' && (coverPdfFile || endPdfFile)) {
          const fd = new FormData();
          fd.append('payload', JSON.stringify(body));
          if (coverPdfFile) fd.append('coverPdf', coverPdfFile);
          if (endPdfFile) fd.append('endPdf', endPdfFile);
          response = await api.post(endpoint, fd, {
            responseType: 'blob',
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          response = await api.post(endpoint, body, { responseType: 'blob' });
        }

        // בדיקה אם חזרה שגיאה (JSON במקום ZIP)
        if (response.data.type === 'application/json') {
          const text = await response.data.text();
          const errData = JSON.parse(text);
          if (errData.skippedGroups) {
            const labels = errData.skippedGroups.map(g => g.label).join(', ');
            throw new Error(`${errData.error}: ${labels}`);
          }
          throw new Error(errData.error || 'שגיאה בהנפקה');
        }

        // בדיקה אם יש קבוצות שדולגו (הגיע בהצלחה אבל חלק מהבחינות דולגו)
        const skippedHeader = response.headers['x-skipped-groups'];
        if (skippedHeader) {
          try {
            const skipped = JSON.parse(decodeURIComponent(skippedHeader));
            if (skipped.length) {
              setSkippedWarning(skipped);
            }
          } catch {}
        }

        const zipName = issuanceName.trim()
          ? `${issuanceName.trim()}.zip`
          : `הנפקת-בחינות-${formatHebrewDate(new Date())}.zip`;
        saveAs(response.data, zipName);
        setGenerated(true);
      }
    } catch (err) {
      // When responseType='blob', axios error responses contain a Blob instead of parsed JSON
      if (err.response?.data instanceof Blob && err.response.data.type === 'application/json') {
        try {
          const text = await err.response.data.text();
          const errData = JSON.parse(text);
          if (errData.skippedGroups?.length) {
            const labels = errData.skippedGroups.map(g => g.label).join(', ');
            setError(`${errData.error}: ${labels}`);
          } else {
            setError(errData.error || 'שגיאה בהנפקה');
          }
        } catch {
          setError('שגיאה בהנפקה');
        }
      } else {
        setError(err.message || err.response?.data?.error || 'שגיאה בהנפקה');
      }
    } finally {
      setGenerating(false);
    }
  };

  const canProceedStep0 = !!(selectedPeriod || selectedExam || hasValidSimanRange);
  const canProceedStep1 = outputType === 'excel' || outputType === 'pdf' || outputType === 'word';

  return (
    <Dialog
      open={open}
      onClose={() => !generating && onClose()}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 3, minHeight: '70vh' } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: 'var(--ck-surface-2, #1a237e)', color: 'var(--ck-text, #fff)', py: 2, borderBottom: '1px solid var(--ck-border, transparent)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PrintIcon />
          <Typography variant="h6" fontWeight={700}>הנפקת בחינות</Typography>
        </Box>
        <IconButton onClick={() => !generating && onClose()} sx={{ color: 'var(--ck-text-2, #fff)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stepper activeStep={activeStep} orientation="vertical" sx={{ p: 3 }}>
          {/* ═══════════ שלב 1: בחירת מועד + סינון ═══════════ */}
          <Step>
            <StepLabel>
              <Typography fontWeight={700}>
                {IS_GEMARA ? 'בחירת מועד מבחנים' : 'בחירת מבחן / מועד'}
              </Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
                {/* בחירת מועד */}
                {IS_GEMARA ? (
                  <Autocomplete
                    options={periods}
                    getOptionLabel={(o) => o.name || ''}
                    value={selectedPeriod}
                    onChange={(_, v) => { setSelectedPeriod(v); setSelectedExam(null); }}
                    renderInput={(params) => (
                      <TextField {...params} label="בחר מועד מבחנים" placeholder="חפש..." />
                    )}
                    renderOption={(props, opt) => (
                      <li {...props} key={opt._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {opt.name}
                          {opt.isActive && <Chip label="פעיל" size="small" color="success" sx={{ fontSize: 11 }} />}
                        </Box>
                      </li>
                    )}
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Autocomplete
                        sx={{ flex: 1 }}
                        options={exams}
                        getOptionLabel={(o) => o.name ? `${o.name} (קוד ${o.code})` : ''}
                        value={selectedExam}
                        onChange={(_, v) => { setSelectedExam(v); setSelectedPeriod(null); }}
                        renderInput={(params) => (
                          <TextField {...params} label="בחר מבחן (אופציונלי)" placeholder="חפש מבחן..." />
                        )}
                      />
                      <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                        או
                      </Typography>
                      <Autocomplete
                        sx={{ flex: 1 }}
                        options={periods}
                        getOptionLabel={(o) => o.name || ''}
                        value={selectedPeriod}
                        onChange={(_, v) => { setSelectedPeriod(v); setSelectedExam(null); }}
                        renderInput={(params) => (
                          <TextField {...params} label="בחר מועד (אופציונלי)" placeholder="חפש מועד..." />
                        )}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      אפשר גם להנפיק לפי טווח סימנים בלבד — בלי מבחן, עיר או כולל.
                    </Typography>
                  </Box>
                )}

                {/* מקור כמויות — הלכתא בלבד */}
                {!IS_GEMARA && (
                  <FormControl>
                    <FormLabel sx={{ fontWeight: 600, mb: 1 }}>מקור כמויות להנפקה</FormLabel>
                    <RadioGroup
                      value={sourceMode}
                      onChange={(e) => setSourceMode(e.target.value)}
                    >
                      <FormControlLabel
                        value="both"
                        control={<Radio size="small" />}
                        label="הרשמות + הזמנות גבאים"
                      />
                      <FormControlLabel
                        value="gabbai"
                        control={<Radio size="small" />}
                        label="רק הזמנות גבאים"
                      />
                      <FormControlLabel
                        value="registrations"
                        control={<Radio size="small" />}
                        label="רק הרשמות"
                      />
                    </RadioGroup>
                  </FormControl>
                )}

                {/* סינון לפי עיר */}
                <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }} elevation={0} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FilterListIcon sx={{ color: 'var(--ck-primary, #1565c0)', fontSize: 20 }} />
                      <Typography fontWeight={600} fontSize={14}>
                        סינון לפי עיר
                        {selectedCities.length > 0 && (
                          <Chip
                            label={`${selectedCities.length} ערים נבחרו`}
                            size="small"
                            color="primary"
                            sx={{ mr: 1, fontSize: 11 }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Autocomplete
                      multiple
                      options={cities}
                      value={selectedCities}
                      onChange={(_, v) => setSelectedCities(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="סנן לפי ערים" placeholder="בחר ערים..." size="small" />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((v, i) => (
                          <Chip {...getTagProps({ index: i })} key={v} label={v} size="small" />
                        ))
                      }
                    />
                  </AccordionDetails>
                </Accordion>

                {/* סינון לפי כולל */}
                <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' } }} elevation={0} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupsIcon sx={{ color: 'var(--ck-primary, #1565c0)', fontSize: 20 }} />
                      <Typography fontWeight={600} fontSize={14}>
                        סינון לפי כולל
                        {selectedKolels.length > 0 && (
                          <Chip
                            label={`${selectedKolels.length} כוללים נבחרו`}
                            size="small"
                            color="primary"
                            sx={{ mr: 1, fontSize: 11 }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Autocomplete
                      multiple
                      options={kolels}
                      value={selectedKolels}
                      onChange={(_, v) => setSelectedKolels(v)}
                      getOptionLabel={(o) => (o.code != null ? `${o.code} — ${o.name || ''}` : (o.name || ''))}
                      isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
                      renderInput={(params) => (
                        <TextField {...params} label="סנן לפי כוללים" placeholder="בחר כוללים..." size="small" />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((v, i) => (
                          <Chip
                            {...getTagProps({ index: i })}
                            key={v._id}
                            label={v.code != null ? `${v.code} — ${v.name || ''}` : (v.name || '')}
                            size="small"
                          />
                        ))
                      }
                    />
                  </AccordionDetails>
                </Accordion>

                {/* טווח סימנים — הלכתא בלבד; מספיק לבדו כדי להמשיך */}
                {!IS_GEMARA && (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography fontWeight={600}>טווח סימנים</Typography>
                      <Button size="small" variant="outlined" onClick={addLink}>+ הוסף חלק</Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      מספיק לבדו להנפקה (בלי מבחן / עיר / כולל). אם נבחר גם מבחן — מחליף את שיוך הספרייה להנפקה זו בלבד.
                    </Typography>
                    {libraryLinks.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        לא הוגדר טווח — נדרש מבחן/מועד, וייעשה שימוש בשיוך הספרייה של המבחן.
                      </Typography>
                    )}
                    {libraryLinks.map((link, li) => (
                      <Box
                        key={li}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1.5 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <TextField
                            select
                            label="חלק"
                            size="small"
                            sx={{ minWidth: 160 }}
                            value={link.chelek || ''}
                            onChange={(e) => changeLinkChelek(li, e.target.value)}
                          >
                            {CHELEK_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                          </TextField>
                          <Button size="small" variant="text" onClick={() => addRange(li)} disabled={!link.chelek}>
                            + רצף
                          </Button>
                          <IconButton size="small" color="error" onClick={() => removeLink(li)} title="הסר חלק">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {(link.ranges || []).map((range, ri) => (
                          <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: 1 }}>
                            <TextField
                              label="מסימן"
                              size="small"
                              type="number"
                              inputProps={{ min: 1, step: 1 }}
                              sx={{ width: 110 }}
                              value={range.simanFrom || ''}
                              onChange={(e) => changeRange(li, ri, 'simanFrom', e.target.value.replace(/\D/g, ''))}
                              disabled={!link.chelek}
                            />
                            <Typography variant="body2" color="text.secondary">—</Typography>
                            <TextField
                              label="עד סימן"
                              size="small"
                              type="number"
                              inputProps={{ min: 1, step: 1 }}
                              sx={{ width: 110 }}
                              value={range.simanTo || ''}
                              onChange={(e) => changeRange(li, ri, 'simanTo', e.target.value.replace(/\D/g, ''))}
                              disabled={!link.chelek}
                            />
                            {(link.ranges || []).length > 1 && (
                              <IconButton size="small" onClick={() => removeRange(li, ri)} title="הסר רצף">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Paper>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canProceedStep0}
                    endIcon={<ArrowBackIcon />}
                    sx={{ bgcolor: 'var(--ck-primary, #1565c0)', '&:hover': { bgcolor: 'var(--ck-primary-hover, #0d47a1)' }, borderRadius: 2 }}
                  >
                    שלב הבא
                  </Button>
                </Box>
              </Box>
            </StepContent>
          </Step>

          {/* ═══════════ שלב 2: סוג פלט + הגדרות ═══════════ */}
          <Step>
            <StepLabel>
              <Typography fontWeight={700}>בחירת סוג הנפקה</Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
                {/* שם הנפקה (אופציונלי) */}
                <TextField
                  fullWidth
                  label="שם להנפקה (אופציונלי)"
                  value={issuanceName}
                  onChange={(e) => setIssuanceName(e.target.value)}
                  placeholder="לדוג׳: הנפקת ניסן תשפ״ו — ערים מרכז"
                  size="small"
                  helperText="ישמש כשם תיקיית ה-ZIP ולזיהוי בהיסטוריה"
                />

                {/* בחירת סוג */}
                <FormControl>
                  <FormLabel sx={{ fontWeight: 600, mb: 1 }}>סוג הנפקה</FormLabel>
                  <RadioGroup
                    row
                    value={outputType}
                    onChange={(e) => setOutputType(e.target.value)}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2, mr: 2, borderRadius: 2, cursor: 'pointer',
                        borderColor: outputType === 'pdf' ? 'var(--ck-primary, #1565c0)' : 'var(--ck-border, #e0e0e0)',
                        bgcolor: outputType === 'pdf' ? 'var(--ck-primary-soft, #e3f2fd)' : 'transparent',
                        transition: 'all 0.15s',
                      }}
                      onClick={() => setOutputType('pdf')}
                    >
                      <FormControlLabel
                        value="pdf"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PictureAsPdfIcon sx={{ color: 'var(--ck-danger, #e53935)' }} />
                            <Box>
                              <Typography fontWeight={600}>הדפסת בחינה מוכנה</Typography>
                              <Typography variant="caption" color="text.secondary">
                                PDF עם בלאנק, כותרת ושאלות
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2, mr: 2, borderRadius: 2, cursor: 'pointer',
                        borderColor: outputType === 'excel' ? 'var(--ck-success, #43a047)' : 'var(--ck-border, #e0e0e0)',
                        bgcolor: outputType === 'excel' ? 'var(--ck-success-soft, #e8f5e9)' : 'transparent',
                        transition: 'all 0.15s',
                      }}
                      onClick={() => setOutputType('excel')}
                    >
                      <FormControlLabel
                        value="excel"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TableChartIcon sx={{ color: 'var(--ck-success, #43a047)' }} />
                            <Box>
                              <Typography fontWeight={600}>ייצוא ל-Excel</Typography>
                              <Typography variant="caption" color="text.secondary">
                                רשימת נבחנים עם פרטים לייצוא
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2, borderRadius: 2, cursor: 'pointer',
                        borderColor: outputType === 'word' ? 'var(--ck-primary, #1565c0)' : 'var(--ck-border, #e0e0e0)',
                        bgcolor: outputType === 'word' ? 'var(--ck-primary-soft, #e3f2fd)' : 'transparent',
                        transition: 'all 0.15s',
                      }}
                      onClick={() => setOutputType('word')}
                    >
                      <FormControlLabel
                        value="word"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ color: 'var(--ck-primary, #1565c0)' }} />
                            <Box>
                              <Typography fontWeight={600}>ייצוא ל-Word</Typography>
                              <Typography variant="caption" color="text.secondary">
                                לעריכת שאלות בכוללים
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </Paper>
                  </RadioGroup>
                </FormControl>

                {/* טקסט בתחילה/סוף — ל-PDF ו-Word */}
                {(outputType === 'pdf' || outputType === 'word') && (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5 }}>טקסט חופשי על הבחינה</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="טקסט בתחילת המבחן"
                      value={introText}
                      onChange={(e) => setIntroText(e.target.value)}
                      placeholder='לדוג׳: מי שיש לו שני מבחנים עונה רק על שאלות עם כוכבית'
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="טקסט בסוף המבחן"
                      value={outroText}
                      onChange={(e) => setOutroText(e.target.value)}
                      placeholder="הערות והארות לבודקים ולמערכת"
                      size="small"
                      helperText={!IS_GEMARA && selectedExam ? 'ממולא אוטומטית מהמבחן — ניתן לערוך' : undefined}
                    />
                  </Paper>
                )}

                {/* סינון שאלות — לכל סוגי הפלט */}
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography fontWeight={600} sx={{ mb: 1.5 }}>סינון שאלות</Typography>
                  <Autocomplete
                    multiple
                    options={difficulties}
                    value={selectedDifficulties}
                    onChange={(_, v) => setSelectedDifficulties(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="רמות קושי"
                        placeholder={selectedDifficulties.length ? '' : 'הכל (ללא סינון)'}
                        size="small"
                        helperText="ריק = כל רמות הקושי"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                      ))
                    }
                  />
                </Paper>

                {/* כוכבית לפי דילוג — PDF / Word */}
                {(outputType === 'pdf' || outputType === 'word') && (
                  <StarEveryNSettings
                    starEveryN={starEveryN}
                    setStarEveryN={setStarEveryN}
                    starEveryNRules={starEveryNRules}
                    setStarEveryNRules={setStarEveryNRules}
                  />
                )}

                {outputType === 'word' && (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5 }}>הגדרות Word</Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox checked={includeTitle} onChange={(e) => setIncludeTitle(e.target.checked)} />}
                        label="הצג כותרת מבחן"
                      />
                      {!IS_GEMARA && (
                        <FormControlLabel
                          control={<Checkbox checked={showSimanimHeader} onChange={(e) => setShowSimanimHeader(e.target.checked)} />}
                          label="הצג סימני המבחן בתחילה (בגדול)"
                        />
                      )}
                    </FormGroup>
                    <MaxQuestionsSettings
                      maxQuestions={maxQuestions}
                      setMaxQuestions={setMaxQuestions}
                      maxQuestionRules={maxQuestionRules}
                      setMaxQuestionRules={setMaxQuestionRules}
                    />
                  </Paper>
                )}

                {/* הגדרות PDF */}
                {outputType === 'pdf' && (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5 }}>הגדרות הדפסה</Typography>

                    {/* אלמנטים עיצוביים */}
                    {elementsLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">טוען אלמנטים...</Typography>
                      </Box>
                    ) : designElements.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>אלמנטים עיצוביים</Typography>
                        </Box>

                        {/* slots that are active */}
                        {activeSlots.includes('top') && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Autocomplete
                              options={designElements.filter(e => e.position === 'top')}
                              getOptionLabel={(o) => o.name || ''}
                              value={selectedTop}
                              isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                              onChange={(_, v) => setSelectedTop(v)}
                              renderInput={(params) => (
                                <TextField {...params} label="אלמנט למעלה" placeholder="בחר..." size="small" />
                              )}
                              sx={{ flex: 1 }}
                            />
                            <FormControlLabel
                              control={<Checkbox checked={topFirstOnly} onChange={(e) => setTopFirstOnly(e.target.checked)} size="small" />}
                              label={<Typography variant="caption">דף ראשון בלבד</Typography>}
                              sx={{ whiteSpace: 'nowrap' }}
                            />
                            <IconButton size="small" onClick={() => { setActiveSlots(s => s.filter(x => x !== 'top')); setSelectedTop(null); }} sx={{ color: 'var(--ck-danger, #ef5350)' }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}

                        {activeSlots.includes('bottom') && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Autocomplete
                              options={designElements.filter(e => e.position === 'bottom')}
                              getOptionLabel={(o) => o.name || ''}
                              value={selectedBottom}
                              isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                              onChange={(_, v) => setSelectedBottom(v)}
                              renderInput={(params) => (
                                <TextField {...params} label="אלמנט למטה" placeholder="בחר..." size="small" />
                              )}
                              sx={{ flex: 1 }}
                            />
                            <FormControlLabel
                              control={<Checkbox checked={bottomFirstOnly} onChange={(e) => setBottomFirstOnly(e.target.checked)} size="small" />}
                              label={<Typography variant="caption">דף ראשון בלבד</Typography>}
                              sx={{ whiteSpace: 'nowrap' }}
                            />
                            <IconButton size="small" onClick={() => { setActiveSlots(s => s.filter(x => x !== 'bottom')); setSelectedBottom(null); }} sx={{ color: 'var(--ck-danger, #ef5350)' }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}

                        {activeSlots.includes('frame') && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Autocomplete
                              options={designElements.filter(e => e.position === 'frame')}
                              getOptionLabel={(o) => o.name || ''}
                              value={selectedFrame}
                              isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                              onChange={(_, v) => setSelectedFrame(v)}
                              renderInput={(params) => (
                                <TextField {...params} label="מסגרת" placeholder="בחר..." size="small" />
                              )}
                              sx={{ flex: 1 }}
                            />
                            <FormControlLabel
                              control={<Checkbox checked={frameFirstOnly} onChange={(e) => setFrameFirstOnly(e.target.checked)} size="small" />}
                              label={<Typography variant="caption">דף ראשון בלבד</Typography>}
                              sx={{ whiteSpace: 'nowrap' }}
                            />
                            <IconButton size="small" onClick={() => { setActiveSlots(s => s.filter(x => x !== 'frame')); setSelectedFrame(null); }} sx={{ color: 'var(--ck-danger, #ef5350)' }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}

                        {/* כפתור הוספה — מציג רק סוגים שעדיין לא נבחרו ויש להם אלמנטים */}
                        {(() => {
                          const SLOT_LABELS = { top: 'למעלה', bottom: 'למטה', frame: 'מסגרת' };
                          const available = ['top', 'bottom', 'frame'].filter(
                            pos => !activeSlots.includes(pos) && designElements.some(e => e.position === pos)
                          );
                          if (!available.length) return null;
                          return (
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              {available.map(pos => (
                                <Button
                                  key={pos}
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AddIcon />}
                                  onClick={() => setActiveSlots(s => [...s, pos])}
                                  sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12 }}
                                >
                                  {SLOT_LABELS[pos]}
                                </Button>
                              ))}
                            </Box>
                          );
                        })()}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        אין אלמנטים עיצוביים — ניתן להוסיף בלשונית "אלמנטים עיצוביים"
                      </Typography>
                    )}

                    {/* דפי שער וסיום (PDF) */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        דפי שער וסיום (PDF)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        בחירה מספרייה או העלאה חד־פעמית להנפקה זו. קובץ חד־פעמי גובר על בחירה מהספרייה.
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Autocomplete
                            options={extraPageTemplates.filter((e) => e.position === 'cover')}
                            getOptionLabel={(o) => o.name || ''}
                            value={coverPdfFile ? null : selectedCoverPage}
                            isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                            onChange={(_, v) => {
                              setSelectedCoverPage(v);
                              if (v) setCoverPdfFile(null);
                            }}
                            disabled={!!coverPdfFile}
                            renderInput={(params) => (
                              <TextField {...params} label="דף שער (לפני)" placeholder="בחר מהספרייה..." size="small" />
                            )}
                            sx={{ flex: 1, minWidth: 200 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            sx={{
                              textTransform: 'none',
                              borderColor: coverPdfFile ? 'var(--ck-danger, #c62828)' : undefined,
                              color: coverPdfFile ? 'var(--ck-danger, #c62828)' : undefined,
                            }}
                          >
                            {coverPdfFile ? coverPdfFile.name : 'העלה לקובץ זה'}
                            <input
                              type="file"
                              hidden
                              accept=".pdf,application/pdf"
                              onChange={(e) => {
                                const f = e.target.files?.[0] || null;
                                setCoverPdfFile(f);
                                if (f) setSelectedCoverPage(null);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                          {(coverPdfFile || selectedCoverPage) && (
                            <IconButton
                              size="small"
                              onClick={() => { setCoverPdfFile(null); setSelectedCoverPage(null); }}
                              sx={{ color: 'var(--ck-danger, #ef5350)' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Autocomplete
                            options={extraPageTemplates.filter((e) => e.position === 'end')}
                            getOptionLabel={(o) => o.name || ''}
                            value={endPdfFile ? null : selectedEndPage}
                            isOptionEqualToValue={(opt, val) => opt._id === val?._id}
                            onChange={(_, v) => {
                              setSelectedEndPage(v);
                              if (v) setEndPdfFile(null);
                            }}
                            disabled={!!endPdfFile}
                            renderInput={(params) => (
                              <TextField {...params} label="דף סיום (אחרי)" placeholder="בחר מהספרייה..." size="small" />
                            )}
                            sx={{ flex: 1, minWidth: 200 }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            sx={{
                              textTransform: 'none',
                              borderColor: endPdfFile ? 'var(--ck-danger, #c62828)' : undefined,
                              color: endPdfFile ? 'var(--ck-danger, #c62828)' : undefined,
                            }}
                          >
                            {endPdfFile ? endPdfFile.name : 'העלה לקובץ זה'}
                            <input
                              type="file"
                              hidden
                              accept=".pdf,application/pdf"
                              onChange={(e) => {
                                const f = e.target.files?.[0] || null;
                                setEndPdfFile(f);
                                if (f) setSelectedEndPage(null);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                          {(endPdfFile || selectedEndPage) && (
                            <IconButton
                              size="small"
                              onClick={() => { setEndPdfFile(null); setSelectedEndPage(null); }}
                              sx={{ color: 'var(--ck-danger, #ef5350)' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox checked={includeTitle} onChange={(e) => setIncludeTitle(e.target.checked)} />}
                        label="הצג כותרת מבחן"
                      />
                      {!IS_GEMARA && (
                        <FormControlLabel
                          control={<Checkbox checked={showSimanimHeader} onChange={(e) => setShowSimanimHeader(e.target.checked)} />}
                          label="הצג סימני המבחן בתחילה (בגדול)"
                        />
                      )}
                      <FormControlLabel
                        control={<Checkbox checked={includeExamineeCode} onChange={(e) => setIncludeExamineeCode(e.target.checked)} />}
                        label="הצג שדה קוד נבחן / ברקוד"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={includeExamCode} onChange={(e) => setIncludeExamCode(e.target.checked)} />}
                        label="הצג קוד מבחן"
                      />
                    </FormGroup>

                    <Divider sx={{ my: 1.5 }} />

                    {/* שורות ריקות למענה */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>שורות ריקות למענה:</Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={answerLinesCount}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(20, parseInt(e.target.value) || 0));
                          setAnswerLinesCount(v);
                        }}
                        inputProps={{ min: 0, max: 20, step: 1 }}
                        sx={{ width: 80 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        (0–20 שורות לכל שאלה)
                      </Typography>
                    </Box>

                    <MaxQuestionsSettings
                      maxQuestions={maxQuestions}
                      setMaxQuestions={setMaxQuestions}
                      maxQuestionRules={maxQuestionRules}
                      setMaxQuestionRules={setMaxQuestionRules}
                    />
                  </Paper>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Button onClick={handleBack} startIcon={<ArrowForwardIcon />}>
                    חזור
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!canProceedStep1}
                    endIcon={<ArrowBackIcon />}
                    sx={{ bgcolor: 'var(--ck-primary, #1565c0)', '&:hover': { bgcolor: 'var(--ck-primary-hover, #0d47a1)' }, borderRadius: 2 }}
                  >
                    תצוגה מקדימה
                  </Button>
                </Box>
              </Box>
            </StepContent>
          </Step>

          {/* ═══════════ שלב 3: תצוגה מקדימה + הנפקה ═══════════ */}
          <Step>
            <StepLabel>
              <Typography fontWeight={700}>תצוגה מקדימה והנפקה</Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                {previewLoading && !preview ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      טוען תצוגה מקדימה...
                    </Typography>
                  </Box>
                ) : preview ? (
                  <>
                    {previewLoading && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">מעדכן מספרי שאלות…</Typography>
                      </Box>
                    )}
                    {/* סיכום */}
                    <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: 'var(--ck-surface-2, #f5f5f5)' }}>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">סה"כ רשומים</Typography>
                          <Typography variant="h5" fontWeight={700} color="var(--ck-primary, #1a237e)">
                            {preview.totalRegistered ?? 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">אחרי סינון</Typography>
                          <Typography variant="h5" fontWeight={700} color={preview.isPartial ? 'var(--ck-warning, #e65100)' : 'var(--ck-success, #2e7d32)'}>
                            {preview.totalFiltered ?? 0}
                          </Typography>
                        </Box>
                        {!IS_GEMARA && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">הזמנות גבאים</Typography>
                            <Typography variant="h5" fontWeight={700} color="var(--ck-violet, #6a1b9a)">
                              {preview.totalGabbaiCopies ?? 0}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="caption" color="text.secondary">עותקים ל־PDF</Typography>
                          <Typography variant="h5" fontWeight={700} color="var(--ck-warning, #e65100)">
                            {(preview.groups || []).reduce((s, g) => s + (g.copies || 0), 0)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">קבצים להנפקה</Typography>
                          <Typography variant="h5" fontWeight={700} color="var(--ck-primary, #1565c0)">
                            {preview.groups?.length || 0}
                          </Typography>
                        </Box>
                        {preview.isPartial && (
                          <Chip label="הנפקה חלקית" color="warning" sx={{ alignSelf: 'center' }} />
                        )}
                        {!IS_GEMARA && preview.sourceMode && (
                          <Chip
                            label={
                              preview.sourceMode === 'both' ? 'הרשמות + גבאים'
                                : preview.sourceMode === 'gabbai' ? 'רק גבאים'
                                  : 'רק הרשמות'
                            }
                            size="small"
                            sx={{ alignSelf: 'center' }}
                          />
                        )}
                      </Box>
                    </Paper>

                    {/* טבלת קבוצות */}
                    {(preview.groups || []).some(g => g.hasQuestions === false) && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        <Typography fontWeight={600} fontSize={14}>
                          לבחינות הבאות לא ניתן להנפיק — חסרות שאלות או טווח בספרייה:
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {(preview.groups || []).filter(g => g.hasQuestions === false).map((g, i) => (
                            <Typography key={i} variant="body2" color="warning.dark">
                              • {g.label}  ({g.memberCount || 0} נבחנים
                              {!IS_GEMARA && g.gabbaiCopies ? `, ${g.gabbaiCopies} גבאים` : ''})
                              {g.questionsReasonLabel ? ` — ${g.questionsReasonLabel}` : ''}
                            </Typography>
                          ))}
                        </Box>
                      </Alert>
                    )}

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'var(--ck-primary-soft, #e8eaf6)' }}>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>תוכן הבחינה</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 90 }}>שאלות</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 120 }}>מקס' שאלות</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 120 }}>דילוג כוכבית</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 100 }}>נבחנים</TableCell>
                            {!IS_GEMARA && (
                              <TableCell sx={{ fontWeight: 700, width: 120 }}>הזמנות גבאים</TableCell>
                            )}
                            <TableCell sx={{ fontWeight: 700, width: 110 }}>עותקים ל־PDF</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(preview.groups || []).map((g, idx) => {
                            const gKey = g.groupKey || (g.exam?._id ? `exam:${g.exam._id}` : g.tractate ? `tractate:${g.tractate}` : 'no-exam');
                            const hasOverride = Object.prototype.hasOwnProperty.call(maxQuestionsByGroup, gKey);
                            const hasStarOverride = Object.prototype.hasOwnProperty.call(starEveryNByGroup, gKey);
                            const applied = g.appliedMaxQuestions;
                            const appliedStar = g.appliedStarEveryN;
                            return (
                            <TableRow key={gKey || idx} hover sx={g.hasQuestions === false ? { bgcolor: 'var(--ck-warning-soft, #fff3e0)', opacity: 0.7 } : {}}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>
                                <Typography fontWeight={600}>
                                  {g.label}
                                  {g.hasQuestions === false && (
                                    <Chip label="אין שאלות" size="small" color="warning" sx={{ mr: 1, fontSize: 11 }} />
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={g.questionCount ?? '—'}
                                  size="small"
                                  sx={{ bgcolor: 'var(--ck-primary-soft, #e3f2fd)', color: 'var(--ck-primary, #1565c0)', fontWeight: 700 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={hasOverride ? maxQuestionsByGroup[gKey] : ''}
                                  placeholder={applied ? String(applied) : 'ללא'}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setMaxQuestionsByGroup((prev) => {
                                      const next = { ...prev };
                                      if (raw === '') {
                                        delete next[gKey];
                                      } else {
                                        next[gKey] = Math.max(0, parseInt(raw, 10) || 0);
                                      }
                                      return next;
                                    });
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{ width: 88 }}
                                  title="ריק = לפי ברירת מחדל / טווח קוד; 0 = ללא הגבלה למבחן זה"
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={hasStarOverride ? starEveryNByGroup[gKey] : ''}
                                  placeholder={appliedStar ? String(appliedStar) : 'ללא'}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setStarEveryNByGroup((prev) => {
                                      const next = { ...prev };
                                      if (raw === '') {
                                        delete next[gKey];
                                      } else {
                                        const v = Math.max(0, parseInt(raw, 10) || 0);
                                        next[gKey] = v < 2 ? 0 : v;
                                      }
                                      return next;
                                    });
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{ width: 88 }}
                                  title="ריק = לפי ברירת מחדל / טווח קוד; 0 = ללא כוכבית; ≥2 = כל N שאלות"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={<GroupsIcon />}
                                  label={g.memberCount ?? 0}
                                  size="small"
                                  sx={{ bgcolor: 'var(--ck-success-soft, #e8f5e9)', color: 'var(--ck-success, #2e7d32)' }}
                                />
                              </TableCell>
                              {!IS_GEMARA && (
                                <TableCell>
                                  <Chip
                                    label={g.gabbaiCopies ?? 0}
                                    size="small"
                                    sx={{ bgcolor: 'var(--ck-violet-soft, #f3e5f5)', color: 'var(--ck-violet, #6a1b9a)', fontWeight: 700 }}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <Chip
                                  label={g.copies ?? 0}
                                  size="small"
                                  sx={{ bgcolor: 'var(--ck-warning-soft, #fff3e0)', color: 'var(--ck-warning, #e65100)', fontWeight: 700 }}
                                />
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Paper>

                    {error && <Alert severity="error">{error}</Alert>}

                    {/* כפתורי הנפקה */}
                    {!generated ? (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Button onClick={handleBack} startIcon={<ArrowForwardIcon />}>
                          חזור
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleGenerate}
                          disabled={generating || !preview.groups?.length}
                          startIcon={generating
                            ? <CircularProgress size={18} color="inherit" />
                            : outputType === 'pdf' ? <PictureAsPdfIcon />
                              : outputType === 'word' ? <DescriptionIcon />
                                : <TableChartIcon />}
                          sx={{
                            bgcolor: outputType === 'pdf' ? 'var(--ck-danger, #c62828)'
                              : outputType === 'word' ? '#1565c0'
                                : '#2e7d32',
                            '&:hover': {
                              bgcolor: outputType === 'pdf' ? 'var(--ck-danger, #b71c1c)'
                                : outputType === 'word' ? '#0d47a1'
                                  : '#1b5e20',
                            },
                            borderRadius: 2,
                            px: 4,
                            py: 1.2,
                            fontSize: 15,
                          }}
                        >
                          {generating
                            ? 'מנפיק...'
                            : outputType === 'pdf'
                              ? `הנפק ${preview.groups?.length || 0} קבצי PDF`
                              : outputType === 'word'
                                ? `הנפק ${preview.groups?.length || 0} קבצי Word`
                                : 'הורד Excel'}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 56, color: 'var(--ck-success, #43a047)', mb: 1 }} />
                        <Typography variant="h6" fontWeight={700} color="var(--ck-success, #2e7d32)">
                          ההנפקה הושלמה בהצלחה!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {preview.isPartial ? 'הנפקה חלקית — ' : ''}
                          {preview.totalFiltered} נבחנים, {preview.groups?.length || 0} קבצים — הקובץ הורד
                        </Typography>

                        {skippedWarning && skippedWarning.length > 0 && (
                          <Alert severity="warning" sx={{ textAlign: 'right', mb: 2 }}>
                            <Typography fontWeight={600} fontSize={14}>
                              הבחינות הבאות דולגו:
                            </Typography>
                            {skippedWarning.map((g, i) => (
                              <Typography key={i} variant="body2" color="warning.dark">
                                • {g.label} ({g.memberCount} נבחנים) — {g.reason}
                              </Typography>
                            ))}
                          </Alert>
                        )}

                        <Button
                          onClick={() => onClose()}
                          sx={{ mt: 2 }}
                        >
                          סגור
                        </Button>
                      </Box>
                    )}
                  </>
                ) : null}
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
    </Dialog>
  );
};

export default ExamIssuanceWizard;
